import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../context/SnackbarContext";
import UsersService from "@velocity-ehs/user-utils-module";
import { EhsPermission } from "../components/routeComponents/EhsConfiguration/EhsRoleTypes";
import { SnackbarType } from "../components/sharedComponents/Snackbar";
import GatewayService from "../services/GatewayService";

export enum RoleActions {
  CREATE = "create",
  UPDATE = "update",
  CLONE = "clone",
}

const useRoleMutations = () => {
  const update = useMutation(
    (req: UpdateRoleInput) => GatewayService.updateRole(req),
    ResponseHandling("ehs_role_update_success")
  );

  const clone = useMutation(
    (req: CloneRoleInput) => GatewayService.cloneRole(req),
    ResponseHandling("ehs_role_clone_success")
  );

  const add = useMutation(
    (req: CreateCustomerRoleInput) => GatewayService.createCustomerRole(req),
    ResponseHandling("ehs_role_create_success")
  );

  const mutate = async (
    action: RoleActions,
    input: RoleFormFields,
    roleId: string,
    policies?: EhsPermission[]
  ) => {
    switch (action) {
      case RoleActions.UPDATE: {
        const updateRole = mapFormToUpdate(roleId, input, policies);
        return await update.mutateAsync(updateRole);
      }

      case RoleActions.CLONE: {
        const cloneRole = mapFormToClone(roleId, input);
        return await clone.mutateAsync(cloneRole);
      }

      case RoleActions.CREATE: {
        const createRole = mapFormToAdd(input, policies);
        return await add.mutateAsync(createRole);
      }

      default:
        throw new Error(`Invalid action: ${action}`);
    }
  };

  return {
    mutations: {
      mutate,
      isAdding: update.isLoading,
      isUpdating: update.isLoading,
      isCloning: clone.isLoading,
    },
  };
};

export default useRoleMutations;

function mapFormToUpdate(
  roleId: string,
  input: RoleFormFields,
  policies: Array<Policy>
): UpdateRoleInput {
  const policyIds = policies.map((policy) => policy.id);

  return {
    customerId: UsersService.customer.id,
    policyIds,
    roleId,
    description: input.description,
    name: input.name,
  };
}

function mapFormToClone(roleId: string, input: RoleFormFields): CloneRoleInput {
  return {
    customerId: UsersService.customer.id,
    roleId,
    description: input.description,
    name: `Copy of ${input.name}`,
  };
}

function mapFormToAdd(
  input: RoleFormFields,
  policies: Array<Policy>
): CreateCustomerRoleInput {
  const policyIds = policies.map((policy) => policy.id);

  return {
    customerId: UsersService.customer.id,
    policies: policyIds,
    description: input.description,
    name: input.name,
  };
}

function ResponseHandling(messageKey: string = "ehs_role_update_success") {
  const { showSnackbar } = useSnackbarActionContext();
  const { t } = useTranslation();

  return {
    onError: (error) => {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    },
    onSuccess: () => {
      showSnackbar(SnackbarType.Success, t(messageKey));
    },
  };
}

export interface UpdateRoleInput {
  customerId: string;
  description?: string;
  name?: string;
  policyIds: Array<string>;
  roleId: string;
}

export interface CreateCustomerRoleInput {
  customerId: string;
  description?: string;
  name?: string;
  policies: Array<string>;
}

export interface CloneRoleInput {
  customerId: string;
  description?: string;
  name: string;
  roleId: string;
}

export interface RoleFormFields {
  name?: string;
  description?: string;
}

export interface Policy {
  id: string;
  name: string;
}
